import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, FileTextIcon, CheckIcon, QuoteIcon, ChevronDownIcon, ShieldCheckIcon, ZapIcon, GlobeIcon, UsersIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Card, CardBody } from './ui/Card';
const kitContents = ['Executive biography (1-page and 2-page formats)', 'Leadership philosophy statement', 'Core competencies and expertise matrix', 'Selected case study summaries', 'Speaking and advisory credentials', 'Professional headshot and brand assets'];
const tools = ['Adobe Creative Cloud', 'Figma', 'AI/GAI Workflows', 'MLAI Integration', 'Jira', 'Azure DevOps', 'Wrike', 'Asana', 'IBM Carbon', 'Material UI'];
const compliance = ['Section 508', 'WCAG 2.1 AA', 'ADA Standards'];
const omniChannelDomains = ['Web & Mobile', 'eCommerce', 'Fintech', 'D2C / B2C / B2B', 'Government'];
const brandClients = ['Walmart', 'Pedigree', 'Samsung', 'Neato', 'Hoover', 'Fortune 500'];
const industries = ['Executive Government', 'Department of Defense', 'B2B / B2C SaaS', 'eCommerce & Retail', 'Finance', 'Construction', 'Oil & Gas', 'Information Technology'];
const testimonials = [{
  quote: "Mike is a Pegasus! One of the most talented professionals I've ever worked with. His artistry, leadership, and ability to bring cohesion to chaotic situations set him apart.",
  attribution: 'Executive Colleague'
}, {
  quote: 'He translates complex business problems into scalable, high-impact products, aligning stakeholders around a shared vision that delivers measurable outcomes.',
  attribution: 'Senior Stakeholder'
}, {
  quote: "Michael's work emphasizes not just aesthetics but building trust, loyalty, and momentum for lasting impact.",
  attribution: 'Industry Partner'
}];
const bioSections = [{
  id: 'strategic',
  icon: UsersIcon,
  title: 'Strategic & Creative Leadership',
  content: 'Michael brings a rare combination of strategic leadership and hands-on design expertise to enterprise-scale initiatives. He has led end-to-end product lifecycles from 0→1 discovery through design system implementation, scalable delivery, and measurable business impact. His work spans cross-platform UX/UI, omni-channel customer journeys, and enterprise design systems such as IBM Carbon and Material UI.'
}, {
  id: 'brand',
  icon: ZapIcon,
  title: 'Brand & Marketing Leadership',
  content: 'Michael has held leadership roles as Brand Manager, Art/Creative Director, and Marketing Manager for organizations ranging from startups to Fortune 500 companies. He has developed enterprise-wide branding initiatives and messaging strategies, designed and maintained global brand identity systems, partnered with cross-functional teams to align stakeholders and scale high-quality outputs, and guided early-stage founders in establishing market presence while facilitating enterprise digital transformation.'
}, {
  id: 'philosophy',
  icon: GlobeIcon,
  title: 'Vision & Philosophy',
  content: 'Guided by faith, service, and a passion for design, Michael is committed to empowering entrepreneurs, organizations, and teams to articulate their vision and execute with clarity. He thrives in senior leadership roles where strategy, craft, and execution converge, delivering meaningful experiences that resonate with users and elevate brands.'
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut'
    }
  }
};
export function ExecutiveBioKit() {
  const [bioExpanded, setBioExpanded] = useState(false);
  const PDF_URL = '/Michael_K_Bigos_Executive_Bio.pdf';
  return <section id="bio-kit" className="py-24 lg:py-32 bg-[#F7F7F5]" aria-labelledby="bio-kit-heading" data-id="element-283">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-284">
        {/* Section eyebrow */}
        <div className="mb-12" data-id="element-285">
          <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-286">
            <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-287" />
            Executive Bio Kit
          </span>
          <h2 id="bio-kit-heading" className="font-heading font-bold text-[#1C1F23] text-3xl lg:text-4xl mb-4" data-id="element-288">
            Michael K. Bigos Jr.
          </h2>
          <p className="font-body text-[#4A5A66] max-w-3xl leading-relaxed" data-id="element-289">
            Seasoned brand strategist, UX/product design leader, and creative
            visionary with over 20 years of experience delivering transformative
            enterprise and consumer-facing solutions. A U.S. Army veteran,
            Michael combines mission-driven discipline with human-centered
            design thinking.
          </p>
        </div>

        {/* Download CTA card */}
        <motion.div className="bg-[#1C1F23] rounded-2xl overflow-hidden mb-16" initial={{
        opacity: 0,
        y: 32
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-290">
          <div className="grid grid-cols-1 lg:grid-cols-5" data-id="element-291">
            {/* Left panel */}
            <div className="lg:col-span-3 p-8 lg:p-12" data-id="element-292">
              <span className="inline-flex items-center gap-2 text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-293">
                <span className="w-6 h-px bg-[#B08D57]" aria-hidden="true" data-id="element-294" />
                Download Package
              </span>
              <h3 className="font-heading font-bold text-[#F7F7F5] text-2xl lg:text-3xl mb-4" data-id="element-295">
                Everything You Need to Evaluate a Senior Leader
              </h3>
              <p className="font-body font-light text-[#ADBEC8] leading-relaxed mb-8" data-id="element-296">
                A comprehensive package designed for executive recruiters,
                search committees, and senior hiring teams. Formatted for
                immediate use in candidate evaluation, board presentations, and
                stakeholder review.
              </p>
              <Button variant="primary" size="lg" as="a" href={PDF_URL} download="Michael_K_Bigos_Executive_Bio.pdf" leftIcon={<DownloadIcon className="w-5 h-5" aria-hidden="true" data-id="element-298" />} aria-label="Download Executive Bio Kit — PDF package" data-id="element-297">
                Download Executive Bio Kit
              </Button>
              <p className="mt-3 font-body text-[#8FA4B0] text-xs" data-id="element-299">
                PDF · Instant download · No registration required
              </p>
            </div>

            {/* Right panel — kit contents */}
            <div className="lg:col-span-2 bg-[#2F3A45]/50 p-8 lg:p-10 flex flex-col justify-center" data-id="element-300">
              <div className="flex items-center gap-2 mb-6" data-id="element-301">
                <FileTextIcon className="w-4 h-4 text-[#B08D57]" aria-hidden="true" data-id="element-302" />
                <h4 className="font-heading font-semibold text-[#F7F7F5] text-sm uppercase tracking-widest" data-id="element-303">
                  Kit Contents
                </h4>
              </div>
              <ul className="space-y-3" role="list" aria-label="Executive Bio Kit contents" data-id="element-304">
                {kitContents.map((item, i) => <motion.li key={i} className="flex items-start gap-3" initial={{
                opacity: 0,
                x: 16
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.08,
                duration: 0.4
              }} data-id="element-305">
                    <div className="w-5 h-5 rounded-full bg-[#B08D57]/20 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-306">
                      <CheckIcon className="w-3 h-3 text-[#B08D57]" data-id="element-307" />
                    </div>
                    <span className="font-body text-[#C4D4DC] text-sm leading-relaxed" data-id="element-308">
                      {item}
                    </span>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Full biography — document style, always visible */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="bg-white border border-[#D8D8D4] rounded-2xl shadow-sm overflow-hidden" data-id="element-309">
          {/* Doc header */}
          <div className="bg-[#1C1F23] px-8 lg:px-12 py-6 flex items-center justify-between" data-id="element-310">
            <div data-id="element-311">
              <p className="text-[#B08D57] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-312">
                Executive Biography
              </p>
              <h3 className="font-heading font-bold text-[#F7F7F5] text-xl lg:text-2xl" data-id="element-313">
                Michael K. Bigos Jr.
              </h3>
            </div>
            <div className="hidden sm:flex items-center gap-2 bg-[#2F3A45] rounded-lg px-4 py-2" data-id="element-314">
              <FileTextIcon className="w-4 h-4 text-[#B08D57]" aria-hidden="true" data-id="element-315" />
              <span className="font-body text-[#ADBEC8] text-xs tracking-widest uppercase" data-id="element-316">
                Full Bio
              </span>
            </div>
          </div>

          {/* Doc body */}
          <div className="px-8 lg:px-12 py-10 space-y-10 font-body text-[#2C3A44]" data-id="element-317">
            {/* Intro */}
            <div data-id="element-318">
              <p className="text-base lg:text-lg leading-relaxed text-[#2C3A44]" data-id="element-319">
                Michael K. Bigos Jr. is a seasoned brand strategist, UX/product
                design leader, and creative visionary with over{' '}
                <strong data-id="element-320">20 years of experience</strong> delivering
                transformative enterprise and consumer-facing solutions. A{' '}
                <strong data-id="element-321">U.S. Army veteran</strong>, Michael combines
                mission-driven discipline with human-centered design thinking,
                empowering organizations to turn complex challenges into clear,
                scalable, high-impact experiences.
              </p>
            </div>

            <hr className="border-[#E8E8E4]" data-id="element-322" />

            {/* Strategic & Creative Leadership */}
            <div data-id="element-323">
              <div className="flex items-center gap-3 mb-4" data-id="element-324">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-325" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-326">
                  Strategic & Creative Leadership
                </h4>
              </div>
              <p className="text-base leading-relaxed text-[#4A5A66]" data-id="element-327">
                Michael brings a rare combination of strategic leadership and
                hands-on design expertise to enterprise-scale initiatives. He
                has led end-to-end product lifecycles from{' '}
                <strong data-id="element-328">0→1 discovery</strong> through design system
                implementation, scalable delivery, and measurable business
                impact. His work spans cross-platform UX/UI, omni-channel
                customer journeys, and enterprise design systems such as{' '}
                <strong data-id="element-329">IBM Carbon</strong> and <strong data-id="element-330">Material UI</strong>.
              </p>
            </div>

            {/* Design & Product Expertise */}
            <div data-id="element-331">
              <div className="flex items-center gap-3 mb-4" data-id="element-332">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-333" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-334">
                  Design & Product Expertise
                </h4>
              </div>
              <div className="space-y-4 text-[#4A5A66]" data-id="element-335">
                <div data-id="element-336">
                  <p className="font-semibold text-[#1C1F23] text-sm mb-1" data-id="element-337">
                    Systems & Tools
                  </p>
                  <p className="text-base leading-relaxed" data-id="element-338">
                    Adobe Creative Cloud, Figma, AI-driven design workflows
                    (GAI, MLAI), Jira, Azure, Wrike, Asana.
                  </p>
                </div>
                <div data-id="element-339">
                  <p className="font-semibold text-[#1C1F23] text-sm mb-1" data-id="element-340">
                    Compliance & Accessibility
                  </p>
                  <p className="text-base leading-relaxed" data-id="element-341">
                    508, WCAG, ADA standards.
                  </p>
                </div>
                <div data-id="element-342">
                  <p className="font-semibold text-[#1C1F23] text-sm mb-1" data-id="element-343">
                    Scalable Systems
                  </p>
                  <p className="text-base leading-relaxed" data-id="element-344">
                    Reusable components, interaction patterns, design system
                    governance.
                  </p>
                </div>
                <div data-id="element-345">
                  <p className="font-semibold text-[#1C1F23] text-sm mb-1" data-id="element-346">
                    Omni-Channel Impact
                  </p>
                  <p className="text-base leading-relaxed" data-id="element-347">
                    Web, mobile, eCommerce, fintech, D2C/B2C/B2B, government
                    initiatives.
                  </p>
                </div>
              </div>
              <p className="mt-5 text-base leading-relaxed text-[#4A5A66]" data-id="element-348">
                Michael translates complex workflows into intuitive, engaging,
                and accessible experiences. He bridges research, strategy, and
                execution, ensuring user-centered outcomes aligned with business
                objectives. His approach balances systems thinking,
                data-informed decisions, and creative intuition to elevate
                adoption, engagement, and operational efficiency.
              </p>
            </div>

            <hr className="border-[#E8E8E4]" data-id="element-349" />

            {/* Brand & Marketing Leadership */}
            <div data-id="element-350">
              <div className="flex items-center gap-3 mb-4" data-id="element-351">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-352" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-353">
                  Brand & Marketing Leadership
                </h4>
              </div>
              <p className="text-base leading-relaxed text-[#4A5A66] mb-4" data-id="element-354">
                Michael has held leadership roles as{' '}
                <strong data-id="element-355">Brand Manager, Art/Creative Director</strong>, and{' '}
                <strong data-id="element-356">Marketing Manager</strong> for organizations ranging
                from startups to Fortune 500 companies — including{' '}
                <strong data-id="element-357">Walmart, Pedigree, Samsung, Neato</strong>, and{' '}
                <strong data-id="element-358">Hoover</strong>. He has:
              </p>
              <ul className="space-y-2.5 text-[#4A5A66]" role="list" data-id="element-359">
                {['Developed enterprise-wide branding initiatives and messaging strategies.', 'Designed and maintained global brand identity systems.', 'Partnered with cross-functional teams to align stakeholders and scale high-quality outputs.', 'Guided early-stage founders in establishing market presence and facilitated enterprise digital transformation.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-360">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B08D57] mt-2 flex-shrink-0" aria-hidden="true" data-id="element-361" />
                    <span className="text-base leading-relaxed" data-id="element-362">{item}</span>
                  </li>)}
              </ul>
            </div>

            <hr className="border-[#E8E8E4]" data-id="element-363" />

            {/* Cross-Industry Impact */}
            <div data-id="element-364">
              <div className="flex items-center gap-3 mb-4" data-id="element-365">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-366" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-367">
                  Cross-Industry Impact
                </h4>
              </div>
              <p className="text-base leading-relaxed text-[#4A5A66] mb-4" data-id="element-368">
                Michael's career spans{' '}
                <strong data-id="element-369">
                  Executive Government, DoD, B2B/B2C, SaaS, eCommerce, retail,
                  finance, construction, oil & gas
                </strong>
                , and <strong data-id="element-370">IT</strong> environments. He excels at:
              </p>
              <ul className="space-y-2.5 text-[#4A5A66]" role="list" data-id="element-371">
                {['Building and modernizing complex digital infrastructures.', 'Elevating and mentoring multi-disciplinary teams.', 'Optimizing acquisition, service, and product journeys.', 'Aligning business objectives with design strategy for measurable results.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-372">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B08D57] mt-2 flex-shrink-0" aria-hidden="true" data-id="element-373" />
                    <span className="text-base leading-relaxed" data-id="element-374">{item}</span>
                  </li>)}
              </ul>
            </div>

            <hr className="border-[#E8E8E4]" data-id="element-375" />

            {/* Testimonials */}
            <div data-id="element-376">
              <div className="flex items-center gap-3 mb-6" data-id="element-377">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-378" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-379">
                  Testimonials
                </h4>
              </div>
              <div className="space-y-5" role="list" aria-label="Testimonials" data-id="element-380">
                {testimonials.map((t, i) => <div key={i} role="listitem" className="bg-[#F7F7F5] border-l-4 border-[#B08D57] rounded-r-xl px-6 py-5" data-id="element-381">
                    <QuoteIcon className="w-5 h-5 text-[#B08D57] mb-3" aria-hidden="true" data-id="element-382" />
                    <blockquote className="text-base leading-relaxed text-[#2C3A44] italic mb-3" data-id="element-383">
                      "{t.quote}"
                    </blockquote>
                    <footer className="text-xs font-semibold uppercase tracking-widest text-[#8FA4B0]" data-id="element-384">
                      — {t.attribution}
                    </footer>
                  </div>)}
              </div>
            </div>

            <hr className="border-[#E8E8E4]" data-id="element-385" />

            {/* Vision & Philosophy */}
            <div data-id="element-386">
              <div className="flex items-center gap-3 mb-4" data-id="element-387">
                <div className="w-1 h-6 bg-[#B08D57] rounded-full flex-shrink-0" aria-hidden="true" data-id="element-388" />
                <h4 className="font-heading font-bold text-[#1C1F23] text-lg lg:text-xl" data-id="element-389">
                  Vision & Philosophy
                </h4>
              </div>
              <p className="text-base leading-relaxed text-[#4A5A66]" data-id="element-390">
                Guided by faith, service, and a passion for design, Michael is
                committed to empowering entrepreneurs, organizations, and teams
                to articulate their vision and execute with clarity. He thrives
                in senior leadership roles where strategy, craft, and execution
                converge, delivering meaningful experiences that resonate with
                users and elevate brands.
              </p>
            </div>

            {/* Bottom download CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-[#1C1F23] rounded-xl p-6 mt-4" data-id="element-391">
              <div data-id="element-392">
                <p className="font-heading font-semibold text-[#F7F7F5] text-base mb-1" data-id="element-393">
                  Ready to connect?
                </p>
                <p className="font-body text-[#ADBEC8] text-sm" data-id="element-394">
                  Download the complete Executive Bio Kit for formatted,
                  print-ready materials.
                </p>
              </div>
              <Button variant="primary" size="md" as="a" href="/Michael_K_Bigos_Executive_Bio.pdf" download="Michael_K_Bigos_Executive_Bio.pdf" leftIcon={<DownloadIcon className="w-4 h-4" aria-hidden="true" data-id="element-396" />} className="flex-shrink-0" aria-label="Download Executive Bio Kit PDF" data-id="element-395">
                Download Bio Kit
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}